import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { TrueTime } from '../../time/true-time.js';
import { VersionRange } from '../core/version-range.js';
import { Address } from './address.js';
import { AddressComponent } from './address-component.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { CustomerProfile } from './customer-profile.js';
import { ContactDetails } from './contact-details.js';
import { Country } from './country.js';
import { DayOfYear } from './day-of-year.js';
import { GeoCoordinates } from './geo-coordinates.js';
import { PhoneNumber } from './phone-number.js';
import { Service } from './service.js';
import { TimeOfDay } from './time-of-day.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import {
  IReservationWithClient,
  IReservationWithContactDetails,
  IReservationWithDate,
  Reservation,
} from '../reservation.js';
import { MonthOfYear } from './month-of-year.js';
import { LocalizedString } from './localized-string.js';
import { Language } from './language.js';

export enum PoolReservationStatus {
    Pending = 'PENDING',
    Approved = 'APPROVED',
    InProgress = 'IN_PROGRESS',
    Finished = 'FINISHED',
    Canceled = 'CANCELED',
}

export class PoolReservation extends Reservation implements IReservationWithClient,
                                                            IReservationWithDate,
                                                            IReservationWithContactDetails {
  static readonly TYPE_ID: string = '6bb99d8a63d54625b3b759dcadf3db68';
  static readonly SCHEMA_VERSION: number = 6;

  public readonly dayId: string;
  public readonly monthId: string;

  private constructor(
        public readonly uniqueId: string,
        public readonly creatorId: string,
        public readonly businessRef: BusinessModuleRef,
        public readonly dayOfYear: DayOfYear,
        public readonly timeOfDay: TimeOfDay,
        public readonly timezone: TimeZoneRegion,
        public readonly creationDate: dayjs.Dayjs,
        public readonly client: CustomerProfile,
        public readonly clientId: string,
        public readonly duration: Duration,
        public readonly status: PoolReservationStatus,
        public readonly service: Service,
        public readonly serviceId: string,
        public readonly isOnlineReservation: boolean,
        public readonly assignedLane: number | null,
        public readonly isLaneReserved: boolean,
        public readonly isPoolReserved: boolean,
        public readonly contactDetails: ContactDetails,
        public readonly hasBeenReviewed: boolean,
        public readonly cancelledDate: dayjs.Dayjs | null,
        public readonly cancelledBy: string | null,
  ) {
    super();
    this.dayId = dayOfYear.uniqueId;
    this.monthId = MonthOfYear.dayOfYearToUniqueId(dayOfYear);
  }

  get $typeId(): string {
    return PoolReservation.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(5, PoolReservation.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status !== PoolReservationStatus.Canceled;
  }

  get isCanceled(): boolean {
    return this.status === PoolReservationStatus.Canceled;
  }

  get date(): dayjs.Dayjs {
    return this.dayOfYear.toDate(TimeZone.ofRegion(this.timezone));
  }

  get time(): dayjs.Dayjs {
    return this.dayOfYear.toDateAndTime(this.timeOfDay, TimeZone.ofRegion(this.timezone));
  }

  get isFinished(): boolean {
    const startDate = this.time;
    const endDate = startDate.add(this.duration);

    return endDate.isBefore(TrueTime.now());
  }

  get isUpcoming(): boolean {
    const startDate = this.time;
    return startDate.isAfter(TrueTime.now());
  }

  get isInProgress(): boolean {
    const now = TrueTime.now();
    const startDate = this.time;
    const endDate = startDate.add(this.duration);

    return (now.isSame(startDate) || now.isAfter(startDate)) && now.isBefore(endDate);
  }

  static fromMap(context: SerializationContext, map: SerializedData): PoolReservation {
    return new PoolReservation(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.fromData<DayOfYear>(MapUtils.get<SerializedData>(map, 'dayOfYear')),
        context.fromData<TimeOfDay>(MapUtils.get<SerializedData>(map, 'timeOfDay')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.fromData<CustomerProfile>(MapUtils.get<SerializedData>(map, 'client')),
        MapUtils.get<string>(map, 'clientId'),
        context.decodeDuration(MapUtils.get<number>(map, 'duration')),
        MapUtils.get<PoolReservationStatus>(map, '$status'),
        context.fromData<Service>(MapUtils.get<SerializedData>(map, 'service')),
        MapUtils.get<string>(map, 'serviceId'),
        MapUtils.get<boolean>(map, 'isOnlineReservation'),
        MapUtils.getOrNull<number>(map, 'assignedLane'),
        MapUtils.get<boolean>(map, 'isLaneReserved'),
        MapUtils.get<boolean>(map, 'isPoolReserved'),
        context.fromData<ContactDetails>(MapUtils.get<SerializedData>(map, 'contactDetails')),
        MapUtils.get<boolean>(map, 'hasBeenReviewed'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'businessRef': this.businessRef.encode(),
      'dayId': this.dayId,
      'dayOfYear': context.toData(this.dayOfYear),
      'timeOfDay': context.toData(this.timeOfDay),
      'duration': context.encodeDuration(this.duration),
      'timezone': this.timezone,
      'creationDate': context.encodeDate(this.creationDate),
      'date': context.encodeDate(this.time),
      'clientId': this.clientId,
      'client': context.toData(this.client),
      '$status': this.status,
      'service': context.toData(this.service),
      'serviceId': this.serviceId,
      'isOnlineReservation': this.isOnlineReservation,
      'assignedLane': this.assignedLane,
      'isLaneReserved': this.isLaneReserved,
      'isPoolReserved': this.isPoolReserved,
      'contactDetails': context.toData(this.contactDetails),
      'hasBeenReviewed': this.hasBeenReviewed,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
    };
  }

  clone(): this {
    return new PoolReservation(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.clone(),
        this.clientId,
        TimeZone.cloneDuration(this.duration),
        this.status,
        this.service.clone(),
        this.serviceId,
        this.isOnlineReservation,
        this.assignedLane,
        this.isLaneReserved,
        this.isPoolReserved,
        this.contactDetails,
        this.hasBeenReviewed,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.dayOfYear, this.dayOfYear) &&
        isSameData(other.timeOfDay, this.timeOfDay) &&
        other.timezone === this.timezone &&
        other.creationDate === this.creationDate &&
        isSameData(other.client, this.client) &&
        other.clientId === this.clientId &&
        other.duration === this.duration &&
        other.status === this.status &&
        isSameData(other.service, this.service) &&
        other.serviceId === this.serviceId &&
        other.isOnlineReservation === this.isOnlineReservation &&
        other.assignedLane === this.assignedLane &&
        other.isLaneReserved === this.isLaneReserved &&
        other.isPoolReserved === this.isPoolReserved &&
        isSameData(other.contactDetails, this.contactDetails) &&
        other.hasBeenReviewed === this.hasBeenReviewed;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new PoolReservation(
        this.uniqueId,
        this.creatorId,
        businessRef.clone(),
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.client.copyWithBusinessRef(businessRef),
        this.clientId,
        TimeZone.cloneDuration(this.duration),
        this.status,
        this.service.copyWithBusinessRef(businessRef),
        this.serviceId,
        this.isOnlineReservation,
        this.assignedLane,
        this.isLaneReserved,
        this.isPoolReserved,
        this.contactDetails,
        this.hasBeenReviewed,
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }
}

export class PoolReservationBuilder extends DataModelBuilder<PoolReservation> {
  build(context: SerializationContext, map: SerializedData): PoolReservation {
    return PoolReservation.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PoolReservation.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PoolReservation.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isLaneReserved', false);
      MapUtils.setIfNullOrUndefined(map, 'isPoolReserved', false);
      version = 2;
    }

    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'contactDetails', context.toData(this.getDefaultPoolContactDetails()));
      version = 3;
    }

    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, 'hasBeenReviewed', false);
      version = 4;
    }

    // In this version [businessId] was replaced with [businessRef]
    if(version < 5) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 5;
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 6) {
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  private getDefaultPoolContactDetails(): ContactDetails {
    return ContactDetails.create({
      names: LocalizedString.forLanguage(Language.Hungarian, 'Gyergyószentmiklósi Uszoda'),
      country: Country.Romania,
      language: Language.Romanian,
      timezone: TimeZoneRegion.EuropeBucharest,
      phoneNumber: PhoneNumber.mustDecode('(+40)748689565'),
      address: Address.create({
        country: 'RO',
        administrativeAreaLevel1: new AddressComponent(
            'ro_hr',
            LocalizedString.forLanguage(Language.Romanian, 'Harghita'),
        ),
        locality: new AddressComponent(
            'ro_hr_gheorgheni',
            LocalizedString.forLanguage(Language.Romanian, 'Gheorgheni'),
        ),
        formattedAddresses: LocalizedString.forLanguage(Language.Hungarian, 'Makkos utca 19/A'),
      }),
      coordinates: new GeoCoordinates(46.7274799, 25.5904378),
      webRoute: null,
      dateSpan: null,
    });
  }
}
